<script setup lang="ts">
import { computed, onUnmounted, ref } from "vue";
import {
  type LucideIcon,
  ChevronRight,
  ClipboardCheck,
  Copy,
  Download,
  ExternalLink,
  Layers,
  LogIn,
  Route,
  Upload,
} from "@lucide/vue";
import CardHeading from "../CardHeading.vue";
import { SPECKLE_CONNECTOR_SERVER_URL, SPECKLE_DOCS_URL, SPECKLE_RELEASES_URL } from "@features/Speckle";

const props = withDefaults(
  defineProps<{
    /** Whether this project already has a stream to publish to; step 3 reads differently before one exists. */
    connected: boolean;
    /** Start folded behind a disclosure header. For the connected state, where the guide is look-up material. */
    collapsible?: boolean;
    /** Lets the last step link straight to the project's Materials page. */
    projectId?: string;
  }>(),
  { collapsible: false, projectId: undefined }
);

const isOpen = ref<boolean>(!props.collapsible);

type GuideStep = { title: string; detail: string; icon: LucideIcon };

const steps = computed<GuideStep[]>(() => [
  {
    title: "Install the connector",
    detail: "Speckle connector for Revit, ArchiCAD or Rhino. Grasshopper uses the Real-Time LCA plugin instead.",
    icon: Download,
  },
  {
    title: "Sign in to the server",
    detail: "In the connector, sign in with the Speckle account linked to your login here.",
    icon: LogIn,
  },
  {
    title: "Publish the model",
    detail: props.connected
      ? "Publish to this project's stream URL, shown above, and pick the model the geometry belongs to."
      : "Create or share a connection above first. The stream URL to publish to appears once it exists.",
    icon: Upload,
  },
  {
    title: "Map the materials",
    detail: "The model's materials appear on the Materials page, ready for mapping. Publish again after a design change.",
    icon: Layers,
  },
]);

// The server address has to be typed into the connector, so it is copyable like the stream URL above.
const wasCopied = ref(false);
let resetCopied: ReturnType<typeof setTimeout> | undefined;
const copyServerUrl = async () => {
  await navigator.clipboard.writeText(SPECKLE_CONNECTOR_SERVER_URL);
  wasCopied.value = true;
  clearTimeout(resetCopied);
  resetCopied = setTimeout(() => (wasCopied.value = false), 2000);
};
onUnmounted(() => clearTimeout(resetCopied));

const stepActionClass =
  "focus-ring rounded-4 t-caption text-app-blue-700 duration-fast ease-standard inline-flex w-fit items-center gap-1 transition-colors hover:underline";
</script>

<template>
  <!-- How the model travels from the authoring tool into this project. Before a connection it is the
       next step and stands open; after one it folds, since the stream URL above is what the reader
       came for. A disclosure header skips the tinted band and its rule (components.md, Step Cards). -->
  <div class="rounded-8 bg-slate-0 p-px-20 flex flex-col gap-3 border border-slate-300">
    <div
      v-if="collapsible"
      class="flex flex-col gap-1">
      <CardHeading class="t-sh4 text-type-default">
        <button
          type="button"
          class="focus-ring rounded-4 -m-1 flex w-full items-center gap-2 p-1 text-left"
          :aria-expanded="isOpen"
          @click="isOpen = !isOpen">
          <ChevronRight
            class="icon-sm text-type-muted duration-fast ease-standard shrink-0 transition-transform"
            :class="{ 'rotate-90': isOpen }"
            :stroke-width="1.5"
            aria-hidden="true" />
          <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
            <Route
              class="icon-sm text-app-blue-600"
              :stroke-width="1.5"
              aria-hidden="true" />
          </span>
          <span>How the model gets in</span>
        </button>
      </CardHeading>
    </div>

    <div
      v-else
      class="-mx-px-20 -mt-px-20 px-px-20 pt-px-20 rounded-t-8 flex flex-col gap-1 border-b border-slate-300 bg-slate-50 pb-3">
      <div class="flex items-center gap-2">
        <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
          <Route
            class="icon-sm text-app-blue-600"
            :stroke-width="1.5"
            aria-hidden="true" />
        </span>
        <CardHeading class="t-sh4 text-type-default">How the model gets in</CardHeading>
      </div>
      <p class="t-caption text-type-muted max-w-prose">
        The first two steps happen once per machine. After that, publish whenever the design changes.
      </p>
    </div>

    <template v-if="isOpen">
      <ol class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <li
          v-for="(step, index) in steps"
          :key="step.title"
          class="rounded-8 flex flex-col gap-2 border border-slate-200 bg-slate-50 p-3">
          <div class="flex items-center gap-2">
            <span
              class="t-caption-bold bg-app-blue-50 text-app-blue-700 flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
              aria-hidden="true"
              >{{ index + 1 }}</span
            >
            <component
              :is="step.icon"
              class="icon-sm text-app-blue-600 shrink-0"
              :stroke-width="1.5"
              aria-hidden="true" />
            <span class="t-small-bold text-type-default">{{ step.title }}</span>
          </div>
          <p class="t-caption text-type-muted grow">{{ step.detail }}</p>

          <a
            v-if="index === 0"
            :href="SPECKLE_RELEASES_URL"
            target="_blank"
            rel="noopener noreferrer"
            :class="stepActionClass">
            <span>Download connector</span>
            <ExternalLink
              class="icon-xs shrink-0"
              :stroke-width="1.5"
              aria-hidden="true" />
          </a>

          <button
            v-else-if="index === 1"
            type="button"
            :class="[stepActionClass, 'max-w-full text-left']"
            :aria-label="wasCopied ? 'Server address copied' : `Copy server address ${SPECKLE_CONNECTOR_SERVER_URL}`"
            @click="copyServerUrl">
            <span class="break-all select-all">{{ SPECKLE_CONNECTOR_SERVER_URL }}</span>
            <ClipboardCheck
              v-if="wasCopied"
              class="icon-xs text-app-green-600 shrink-0"
              :stroke-width="1.5"
              aria-hidden="true" />
            <Copy
              v-else
              class="icon-xs shrink-0"
              :stroke-width="1.5"
              aria-hidden="true" />
          </button>

          <RouterLink
            v-else-if="index === 3 && projectId"
            :to="`/project/${projectId}/mapping`"
            :class="stepActionClass">
            <span>Go to Materials</span>
            <ChevronRight
              class="icon-xs shrink-0"
              :stroke-width="1.5"
              aria-hidden="true" />
          </RouterLink>
        </li>
      </ol>

      <a
        :href="SPECKLE_DOCS_URL"
        target="_blank"
        rel="noopener noreferrer"
        :class="stepActionClass">
        <span>Setup guide per tool</span>
        <ExternalLink
          class="icon-xs shrink-0"
          :stroke-width="1.5"
          aria-hidden="true" />
      </a>
    </template>
  </div>
</template>
